import getAddressSchema from './address'
import { transformAddressToString } from '../address'
import { PracticeProfile } from '../../types/practice'
import getImageFromNextImage from '../getImageFromNextImage'

export default function getPracticeSchema(
  practice: PracticeProfile,
  lang: string,
  locale: string,
  translate: (key: string, options?: Record<string, string | number | boolean> | undefined) => string,
  visibilityComponents: any
) {
  const {
    slug,
    about,
    address,
    specialists,
    insurers,
    reviewsTotal,
    reviewObject,
    workingOpeningHours,
    keywords,
    name,
    images,
    phone,
    type,
    healthChecks
  } = practice || {}
  const practiceType = type === 'clinic' ? 'MedicalClinic' : 'Hospital'
  const pageUrl = `https://www.doctify.com/${locale}/practice/${slug}`
  const addressSchema = getAddressSchema(address, lang)
  const aboutSchema = (about?.[lang] || '')
    .replace(/<\/?[^>]+(>|$)/g, '')
    .replace(/&rsquo;/g, '’')
    .replace(/&nbsp;/g, ' ')

  const geoSchema = address?.geolocation
    ? {
        '@type': 'GeoCoordinates',
        latitude: address?.geolocation?.lat,
        longitude: address?.geolocation?.lon
      }
    : null

  const potentialActionSchema = {
    '@type': 'reserveAction',
    target: {
      '@type': 'EntryPoint',
      urlTemplate: pageUrl,
      actionPlatform: [
        'http://schema.org/DesktopWebPlatform',
        'http://schema.org/IOSPlatform',
        'http://schema.org/AndroidPlatform'
      ],
      inLanguage: lang
    },
    result: {
      '@type': 'Reservation',
      name: translate('book_appointment')
    }
  }

  const employeeSchema = specialists?.map((spec) => ({
    '@type': 'Person',
    name: [spec.firstName?.[lang], spec.lastName?.[lang]].filter((v) => !!v).join(' '),
    url: `https://www.doctify.com/${locale}/specialist/${spec.slug}`,
    sameAs: `https://www.doctify.com/${locale}/specialist/${spec.slug}`,
    image: getImageFromNextImage(spec.images?.logo)
  }))

  const memberOfSchema = insurers?.map((insurer) => ({
    '@type': 'ProgramMembership',
    name: insurer.name,
    url: insurer.website,
    sameAs: insurer.website
  }))

  const aggregateRatingSchema =
    reviewsTotal > 0
      ? {
          '@type': 'AggregateRating',
          '@id': pageUrl,
          identifier: pageUrl,
          bestRating: 5,
          worstRating: 1,
          ratingValue: reviewObject.averageRating,
          reviewCount: reviewsTotal
        }
      : null

  const workingDays: Record<number, string> = {
    1: 'monday',
    2: 'tuesday',
    3: 'wednesday',
    4: 'thursday',
    5: 'friday',
    6: 'saturday',
    7: 'sunday'
  }
  const openingHoursSchema = Object.keys(workingOpeningHours || {})
    .reduce((prev: string[], curr) => {
      if (workingOpeningHours?.[curr]) {
        const hours = workingOpeningHours?.[curr]
          // @ts-ignore
          .map(({ open, close }) => `${translate(workingDays[curr]!)} ${open}-${close}`)
          .join('; ')
        return [...prev, hours]
      }

      return prev
    }, [])
    .join('; ')

  const availableServiceSchema = healthChecks.map((hc) => ({
    '@type': 'MedicalProcedure',
    name: hc.name?.[lang],
    description: hc.description?.[lang],
    image: getImageFromNextImage(hc.iconUri)
  }))
  const makesOfferSchema = healthChecks.map((hc) => ({
    '@type': 'Offer',
    name: hc.name?.[lang],
    description: hc.description?.[lang],
    image: getImageFromNextImage(hc.iconUri),
    price: hc.packagePrice?.length === 1 ? hc.packagePrice : undefined,
    priceSpecification:
      hc.packagePrice?.length === 2
        ? {
            '@type': 'PriceSpecification',
            minPrice: hc.packagePrice[0],
            maxPrice: hc.packagePrice[1]
          }
        : undefined,
    itemOffered: {
      '@type': 'Service',
      url: pageUrl
    }
  }))

  const medicalSpecialtySchema = keywords
    ?.filter((keyword) => keyword.keywordType === 'specialty')
    ?.map((specialty) => ({
      '@type': 'MedicalSpecialty',
      name: specialty.name?.[lang],
      description: specialty.description?.[lang],
      image: getImageFromNextImage(specialty.iconUri)
    }))

  const faqQuestions = [
    visibilityComponents.faqQuestions.faqQuestionReviews
      ? {
          '@type': 'Question',
          name: translate('profile.question_reviews', { name: name?.[lang], skipBold: true }),
          acceptedAnswer: {
            '@type': 'Answer',
            text: [
              translate('profile.answer_reviews', {
                name: name?.[lang],
                rating: reviewObject.averageRating || ''
              }),
              translate('profile.based_on_reviews', {
                name: name?.[lang],
                rating: practice.averageRating
              }),
              translate('reviews.count', { reviews: practice.reviewsTotal, skipBold: true })
            ].join(' ')
          }
        }
      : null,
    visibilityComponents.faqQuestions.faqQuestionWhereLocated
      ? {
          '@type': 'Question',
          name: translate('profile.question_where_located', {
            name: name?.[lang],
            skipBold: true
          }),
          acceptedAnswer: {
            '@type': 'Answer',
            text: translate('profile.practices_located_at', {
              address: transformAddressToString(address, lang),
              practice: name?.[lang],
              skipBold: true
            })
          }
        }
      : null,
    visibilityComponents.faqQuestions.faqQuestionsLocation
      ? {
          '@type': 'Question',
          name: translate('profile.questions_practice_location', {
            name: name?.[lang],
            skipBold: true
          }),
          acceptedAnswer: {
            '@type': 'Answer',
            text: translate('profile.answer_practice_location', {
              hours: openingHoursSchema,
              name: name?.[lang],
              skipBold: true
            })
          }
        }
      : null,
    visibilityComponents.faqQuestions.faqQuestionPracticePhone
      ? {
          '@type': 'Question',
          name: translate('profile.question_practice_phone', {
            name: name?.[lang],
            skipBold: true
          }),
          acceptedAnswer: {
            '@type': 'Answer',
            text: phone
          }
        }
      : null,
    visibilityComponents.faqQuestions.faqQuestionConsultants
      ? {
          '@type': 'Question',
          name: translate('profile.question_consultants', {
            name: name?.[lang],
            skipBold: true
          }),
          acceptedAnswer: {
            '@type': 'Answer',
            text: translate('profile.answer_consultants', {
              name: name?.[lang],
              skipBold: true
            })
          }
        }
      : null,
    visibilityComponents.faqQuestions.faqQuestionAcceptVisits
      ? {
          '@type': 'Question',
          name: translate('profile.question_accept_visits', {
            name: name?.[lang],
            skipBold: true
          }),
          acceptedAnswer: {
            '@type': 'Answer',
            text: [
              translate('profile.answer_accept_visits', { name: name?.[lang] }),
              translate('get_in_touch'),
              translate('profile.short_enquire_as_a_new_patient', { skipBold: true })
            ].join(' ')
          }
        }
      : null
  ].filter(Boolean)

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqQuestions
  }

  const isShowAggregateRatingSchema = !!aggregateRatingSchema && !practice.basic
  const isShowFaqSchema = visibilityComponents.faq

  return JSON.stringify([
    {
      employee: employeeSchema,
      memberOf: memberOfSchema,
      availableService: availableServiceSchema,
      medicalSpecialty: medicalSpecialtySchema,
      makesOffer: makesOfferSchema,
      address: addressSchema,
      '@context': 'http://schema.org',
      '@type': practiceType,
      '@id': pageUrl,
      url: pageUrl,
      name: name?.[lang],
      telephone: phone,
      description: aboutSchema,
      logo: getImageFromNextImage(images?.logo),
      image: getImageFromNextImage(images?.cover || images?.logo),
      geo: geoSchema,
      potentialAction: potentialActionSchema,
      openingHours: openingHoursSchema,
      ...(isShowAggregateRatingSchema ? { aggregateRating: aggregateRatingSchema } : {})
    },
    ...(isShowFaqSchema ? [faqSchema] : [])
  ])
}
